import { useState, useRef, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Info } from 'lucide-react';
import { createPortal } from 'react-dom';

interface ProgressBarProps {
  value: number;
  className?: string;
}

export function ProgressBar({ value, className }: ProgressBarProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const infoRef = useRef<HTMLDivElement>(null);

  // Update tooltip position when info icon is hovered
  const updateTooltipPosition = () => {
    if (infoRef.current) {
      const rect = infoRef.current.getBoundingClientRect();
      setTooltipPosition({
        x: rect.left + rect.width / 2,
        y: rect.top
      });
    }
  };

  // Handle mouse enter with position update
  const handleMouseEnter = () => {
    updateTooltipPosition();
    setIsVisible(true);
  };

  // Update position on window resize if tooltip is visible
  useEffect(() => {
    if (isVisible) {
      const handleResize = () => {
        updateTooltipPosition();
      };

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [isVisible]);

  return (
    <div className={cn("flex items-center gap-2", className)}>

      <div className="relative inline-block">
        <div
          ref={infoRef}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={() => setIsVisible(false)}
          className="cursor-pointer text-gray-400 hover:text-gray-600"
        >
          <Info className="inline-block h-3 w-3 ml-1 text-primary" >
          </Info>
        </div>
        {isVisible && createPortal(
          <div className="fixed z-[9999] w-[85px] px-2 py-1.5 text-xs bg-white rounded-lg shadow-lg border border-gray-100" style={{
            left: `${tooltipPosition.x}px`,
            top: `${tooltipPosition.y - 50}px`,
            transform: 'translateX(-50%)',
            filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))',
          }}>
            {/* Arrow pointing down */}
            <div className="absolute w-2 h-2 bg-white border-r border-b border-gray-100 transform rotate-45 left-1/2 -bottom-1 -ml-1 z-[9999]"></div>
            {/* Progress bar container */}
            <div className="w-full h-1.5 bg-gray-100 rounded-full mb-1.5">
              {/* Progress bar fill */}
              <div
                className="h-full bg-blue-500 rounded-full"
                style={{ width: `${value}%` }}
              />
            </div>

            {/* Text below progress bar */}
            <div className="paragraph-8 font-normal">
              Today Avg. {value}%
            </div>
          </div>,
          document.body
        )}
      </div>
      <div className="w-full max-w-[80px] min-w-[60px] h-1.5 bg-light-grey rounded-full overflow-hidden relative">
        {/* <div
          className="h-1.5 absolute w-1/2 border-r border-black"
        /> */}
        <div
          className="h-full bg-blue-500 rounded-full transition-all duration-300 ease-in-out"
          style={{ width: `${value}%` }}
        />
      </div>
      <span className="paragraph-11 text-primary">{value}%</span>
    </div>
  );
}